import { useState, useEffect } from "react";
import { Loader2, Filter, Shield, MoonStar, Download } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { LOGIN_RESULT, ADMIN_ACTION_LABELS } from "./shared/constants";
import { fmtTs } from "./shared/format";
import { LoadingRow } from "./shared/ui";

export function LogboekAdmin() {
  const [logins, setLogins] = useState([]);
  const [activity, setActivity] = useState([]);
  const [adminActions, setAdminActions] = useState({}); // FIX#15 (B-1)
  const [loading, setLoading] = useState(true);
  const [failedOnly, setFailedOnly] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting] = useState(false);
  const PAGE = 200;

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/admin/audit/logins/export?failed_only=${failedOnly}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "logboek.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      const total = parseInt(res.headers?.["x-total-matching"] || "0", 10);
      if (total > 5000) {
        toast.message(`Export afgekapt op 5000 regels (van ${total} totaal).`);
      } else {
        toast.success("Logboek geëxporteerd.");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExporting(false);
    }
  };

  const loadLogins = async (nextOffset, failed) => {
    const { data } = await api.get(`/admin/audit/logins?failed_only=${failed}&offset=${nextOffset}&limit=${PAGE}`);
    setHasMore(data.length === PAGE);
    return data;
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [lg, ac] = await Promise.all([loadLogins(0, failedOnly), api.get("/admin/audit/activity")]);
      setLogins(lg);
      setActivity(ac.data.users); // FIX#15 (B-1) — response wrapped {users, admin_actions}
      setAdminActions(ac.data.admin_actions || {});
      setOffset(0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [failedOnly]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const next = offset + PAGE;
      const more = await loadLogins(next, failedOnly);
      setLogins((prev) => [...prev, ...more]);
      setOffset(next);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div data-testid="admin-logboek-panel">
      <p className="text-[11px] text-tw-muted leading-relaxed mb-5 flex items-start gap-1.5" data-testid="logboek-privacy-note">
        <Shield size={12} strokeWidth={2.5} className="text-tw-yellow shrink-0 mt-0.5" />
        <span>Alleen gebeurtenissen, geen inhoud. Automatisch opgeschoond na 90 dagen.</span>
      </p>

      {loading ? (
        <LoadingRow />
      ) : (
        <>
          {/* --- Inloggeschiedenis --- */}
          <div className="mb-8">
            <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
              <div className="font-mono text-[10px] tracking-[0.3em] text-tw-muted uppercase">Inloggeschiedenis</div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setFailedOnly((v) => !v)}
                  data-testid="logboek-failed-toggle"
                  className={`tw-press inline-flex items-center gap-1.5 px-2.5 py-1.5 border font-mono text-[10px] uppercase tracking-[0.15em] ${failedOnly ? "text-black bg-tw-yellow border-tw-yellow" : "text-tw-muted border-tw hover:border-tw-yellow hover:text-tw-yellow"}`}
                >
                  <Filter size={11} strokeWidth={2.5} /> Alleen mislukt/geblokkeerd
                </button>
                <button
                  type="button"
                  onClick={exportCsv}
                  disabled={exporting}
                  data-testid="logboek-export-csv-btn"
                  className="tw-press inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-tw text-tw-muted hover:border-tw-yellow hover:text-tw-yellow font-mono text-[10px] uppercase tracking-[0.15em] disabled:opacity-50"
                >
                  {exporting ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} strokeWidth={2.5} />} CSV
                </button>
              </div>
            </div>
            {logins.length === 0 ? (
              <div className="border border-dashed border-tw py-8 text-center text-tw-muted text-sm" data-testid="logboek-logins-empty">
                Nog geen inlogpogingen geregistreerd.
              </div>
            ) : (
              <div className="space-y-1.5" data-testid="logboek-logins-list">
                {logins.map((r) => {
                  const meta = LOGIN_RESULT[r.type] || LOGIN_RESULT.login_failed;
                  return (
                    <div key={r.id} data-testid={`logboek-login-${r.id}`} className="flex items-center gap-3 bg-tw-surface border border-tw px-3 py-2">
                      <span data-testid={`logboek-result-${r.type}`} className="shrink-0 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.15em]" style={{ backgroundColor: meta.bg, color: meta.fg }}>
                        {meta.label}
                      </span>
                      <span className="text-white text-sm truncate flex-1 min-w-0">{r.email || "—"}</span>
                      <span className="font-mono text-[10px] text-tw-muted shrink-0">{fmtTs(r.ts)}</span>
                    </div>
                  );
                })}
                {hasMore && (
                  <button type="button" onClick={loadMore} disabled={loadingMore} data-testid="logboek-load-more"
                    className="tw-press w-full mt-2 py-2 border border-tw text-tw-muted hover:text-tw-yellow hover:border-tw-yellow font-mono text-[10px] uppercase tracking-[0.2em] disabled:opacity-50">
                    {loadingMore ? "Laden…" : "Meer laden"}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* --- Activiteit per teamlid --- */}
          <div>
            <div className="font-mono text-[10px] tracking-[0.3em] text-tw-muted uppercase mb-3">Activiteit per teamlid <span className="text-tw-muted/70">(laatste 30 dagen)</span></div>
            <div className="space-y-2" data-testid="logboek-activity-list">
              {activity.map((u) => (
                <article key={u.user_id} data-testid={`logboek-activity-${u.user_id}`} className="bg-tw-surface border border-tw p-3">
                  <div className="flex items-center gap-2 flex-wrap mb-1.5">
                    <span className="text-white font-medium text-sm">{u.name}</span>
                    <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-tw-muted">{u.role}</span>
                    {u.silent_after_login && (
                      <span data-testid={`logboek-silent-${u.user_id}`} className="inline-flex items-center gap-1 border border-tw-red/60 text-tw-red px-1.5 py-0.5 font-mono text-[9px] tracking-[0.15em] uppercase">
                        <MoonStar size={9} strokeWidth={2.8} /> stil na login
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 flex-wrap font-mono text-[10px] text-tw-muted">
                    <span>Laatste login: <span className="text-white">{fmtTs(u.last_login)}</span></span>
                    <span>Laatste actie: <span className="text-white">{fmtTs(u.last_activity)}</span></span>
                  </div>
                  <div className="flex items-center gap-4 flex-wrap mt-1.5 font-mono text-[10px] text-tw-muted">
                    <span data-testid={`logboek-memos-${u.user_id}`}>Memo's: <span className="text-tw-yellow">{u.memos_30d}</span></span>
                    <span>Verwerkingen: <span className="text-tw-yellow">{u.verwerkingen_30d}</span></span>
                    <span>Brein-vragen: <span className="text-tw-yellow">{u.brein_30d}</span></span>
                    {u.password_changed_30d > 0 && (
                      <span data-testid={`logboek-pwchanged-${u.user_id}`}>Wachtwoord gewijzigd: <span className="text-tw-yellow">{u.password_changed_30d}</span></span>
                    )}
                    {u.memo_replaced_30d > 0 && (
                      <span data-testid={`logboek-replaced-${u.user_id}`}>Opnieuw ingesproken: <span className="text-tw-yellow">{u.memo_replaced_30d}</span></span>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>

          {/* --- Beheeracties (30 dagen) --- FIX#15 (B-1): category CRUD + admin password force-reset */}
          {Object.values(adminActions).some((n) => n > 0) && (
            <div className="mt-6" data-testid="logboek-admin-actions">
              <div className="font-mono text-[10px] tracking-[0.3em] text-tw-muted uppercase mb-3">Beheeracties <span className="text-tw-muted/70">(laatste 30 dagen)</span></div>
              <div className="bg-tw-surface border border-tw p-3 flex flex-wrap gap-4 font-mono text-[10px] text-tw-muted">
                {Object.entries(adminActions).filter(([, n]) => n > 0).map(([type, n]) => (
                  <span key={type} data-testid={`logboek-admin-action-${type}`}>
                    {ADMIN_ACTION_LABELS[type] || type}: <span className="text-tw-yellow">{n}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
